const chapter = {
  id: 'communication-systems',
  slug: 'communication-systems',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main',
  title: {
    english: 'Communication Systems',
        hindi: 'संचार तंत्र',
  },
  estReadMinutes: 35,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1 question (occasional)',
        hindi: '1 प्रश्न (कभी-कभी)',
  },
  syllabusTags: ['Electronics', 'JEE-Main', 'Class-12'],
  sections: [
    {
      id: 'elements',
      title: {
        english: 'Elements of a Communication System',
        hindi: 'संचार तंत्र के तत्व',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Three essential parts: **Transmitter** (processes message + couples to channel), **Channel** (medium — wire, fibre, free space), **Receiver** (recovers message from received signal). Inevitable: noise, attenuation, distortion.',
        hindi: 'तीन आवश्यक भाग: **ट्रांसमीटर** (संदेश संसाधित करता है + चैनल से जोड़े), **चैनल** (मध्यम - तार, फाइबर, मुक्त स्थान), **रिसीवर** (प्राप्त सिग्नल से संदेश पुनर्प्राप्त करता है)। अपरिहार्य: शोर, क्षीणन, विकृति।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Bandwidth** — range of frequencies a signal occupies. Speech ~3 kHz, music ~20 kHz, video ~6 MHz, digital data depends on rate.',
        hindi: '**बैंडविड्थ** - सिग्नल द्वारा व्याप्त आवृत्तियों की सीमा। भाषण ~3 किलोहर्ट्ज़, संगीत ~20 किलोहर्ट्ज़, वीडियो ~6 मेगाहर्ट्ज, डिजिटल डेटा दर पर निर्भर करता है।',
            },
            {
              english: '**Attenuation** — loss of signal strength with distance.',
        hindi: '**क्षीणन** - दूरी के साथ सिग्नल की शक्ति में कमी।',
            },
            {
              english: '**Range** — distance over which signal can be received with acceptable quality.',
        hindi: '**रेंज** - वह दूरी जिस पर स्वीकार्य गुणवत्ता के साथ सिग्नल प्राप्त किया जा सकता है।',
            },
            {
              english: '**Repeater** — receives, amplifies and retransmits signal (used in long-distance comms, satellites).',
        hindi: '**रिपीटर** - सिग्नल प्राप्त करता है, बढ़ाता है और पुनः प्रसारित करता है (लंबी दूरी के कॉम, उपग्रहों में उपयोग किया जाता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'propagation',
      title: {
        english: 'Propagation of EM Waves',
        hindi: 'EM तरंगों का संचरण',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Mode', 'Frequency range', 'Mechanism', 'Use'],
          rows: [
            ['Ground (surface) wave', '$\\le 1500$ kHz', 'Wave guided along earth surface', 'Local AM broadcast'],
            ['Sky wave', '1.5–30 MHz', 'Reflected by ionosphere (F-layer)', 'Short-wave / international AM'],
            ['Space wave / Line-of-sight', '$> 40$ MHz', 'Direct + ground-reflected; needs LOS', 'TV, FM, microwave links, satellite'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Line-of-sight (LOS) range',
        hindi: 'दृष्टि रेखा (एलओएस) सीमा',
          },
          statement: {
            english: 'For an antenna of height $h$ above ground (radius $R$): horizon distance $d = \\sqrt{2 R h}$. For TX height $h_T$ and RX height $h_R$: $d_{\\max} = \\sqrt{2 R h_T} + \\sqrt{2 R h_R}$.',
        hindi: 'जमीन से ऊंचाई $h$ (त्रिज्या $R$) वाले एंटीना के लिए: क्षितिज दूरी $d = \\sqrt{2 R h}$। TX ऊँचाई $h_T$ और RX ऊँचाई $h_R$ के लिए: $d_{\\max} = \\sqrt{2 R h_T} + \\sqrt{2 R h_R}$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Area covered by single antenna of height $h$: $A = \\pi(2 R h) = 2\\pi R h$.',
        hindi: 'ऊंचाई $h$: $A = \\pi(2 R h) = 2\\pi R h$ के एकल एंटीना द्वारा कवर किया गया क्षेत्र।',
            },
            {
              english: 'Population covered: $A \\times $ population density.',
        hindi: 'कवर की गई जनसंख्या: $A \\times $ जनसंख्या घनत्व।',
            },
            {
              english: 'Optimum antenna length (resonant): $\\ell = \\lambda/4$ (quarter-wave) or $\\lambda/2$.',
        hindi: 'इष्टतम एंटीना लंबाई (गुंजयमान यंत्र): $\\ell = \\lambda/4$ (क्वार्टर-वेव) या $\\lambda/2$।',
            },
          ],
        },
      ],
    },
    {
      id: 'modulation',
      title: {
        english: 'Need for Modulation',
        hindi: 'मॉडुलन की आवश्यकता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Audio signals (20 Hz–20 kHz) cannot be radiated directly: antenna size required ($\\lambda/4$) would be impractically huge.',
        hindi: 'ऑडियो सिग्नल (20 हर्ट्ज-20 किलोहर्ट्ज़) सीधे प्रसारित नहीं किए जा सकते: आवश्यक एंटीना आकार ($\\lambda/4$) अव्यवहारिक रूप से बहुत बड़ा होगा।',
            },
            {
              english: 'Multiple signals would overlap in same band → no separation.',
        hindi: 'एकाधिक सिग्नल एक ही बैंड में ओवरलैप होंगे → कोई पृथक्करण नहीं।',
            },
            {
              english: '**Modulation**: low-frequency message rides on a high-frequency **carrier** by varying one of its properties (amplitude, frequency, or phase).',
        hindi: '**मॉड्यूलेशन**: निम्न-आवृत्ति संदेश उच्च-आवृत्ति **वाहक** पर उसके गुणों (आयाम, आवृत्ति, या चरण) में से किसी एक को भिन्न करके चलता है।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Amplitude Modulation (AM)** — carrier amplitude varies with message.',
        hindi: '**आयाम मॉड्यूलेशन (एएम)** - वाहक आयाम संदेश के साथ बदलता रहता है।',
            },
            {
              english: '**Frequency Modulation (FM)** — carrier frequency varies with message; bandwidth larger but noise immunity better.',
        hindi: '**फ़्रीक्वेंसी मॉड्यूलेशन (एफएम)** — वाहक आवृत्ति संदेश के साथ बदलती रहती है; बैंडविड्थ बड़ा लेकिन शोर प्रतिरोधक क्षमता बेहतर।',
            },
            {
              english: '**Phase Modulation (PM)** — carrier phase varies with message.',
        hindi: '**चरण मॉड्यूलेशन (पीएम)** - वाहक चरण संदेश के साथ बदलता रहता है।',
            },
            {
              english: '**Pulse modulation (PAM, PWM, PPM, PCM)** — carrier is a train of pulses.',
        hindi: '**पल्स मॉड्यूलेशन (PAM, PWM, PPM, PCM)** - वाहक दालों की एक ट्रेन है।',
            },
          ],
        },
      ],
    },
    {
      id: 'am',
      title: {
        english: 'Amplitude Modulation in Detail',
        hindi: 'आयाम मॉडुलन विस्तार से',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Carrier $c(t) = A_c \\cos(\\omega_c t)$. Message $m(t) = A_m\\cos(\\omega_m t)$. Modulated wave:',
        hindi: 'वाहक $c(t) = A_c \\cos(\\omega_c t)$. संदेश $m(t) = A_m\\cos(\\omega_m t)$. संग्राहक तरंग:',
          },
        },
        {
          type: 'equation',
          latex: 'c_m(t) = A_c\\cos(\\omega_c t) + \\tfrac{\\mu A_c}{2}\\cos((\\omega_c - \\omega_m) t) + \\tfrac{\\mu A_c}{2}\\cos((\\omega_c + \\omega_m) t).',
        },
        {
          type: 'list',
          items: [
            {
              english: '**Modulation index** $\\mu = A_m/A_c$, $0 \\le \\mu \\le 1$. Equivalent: $\\mu = (A_{\\max} - A_{\\min})/(A_{\\max} + A_{\\min})$.',
        hindi: '**मॉड्यूलेशन इंडेक्स** $\\mu = A_m/A_c$, $0 \\le \\mu \\le 1$. समतुल्य: $\\mu = (A_{\\max} - A_{\\min})/(A_{\\max} + A_{\\min})$.',
            },
            {
              english: 'AM spectrum: carrier $\\omega_c$, **lower side band** $\\omega_c - \\omega_m$, **upper side band** $\\omega_c + \\omega_m$. Bandwidth $= 2\\omega_m$ (twice highest message frequency).',
        hindi: 'AM स्पेक्ट्रम: कैरियर $\\omega_c$, **निचली तरफ का बैंड** $\\omega_c - \\omega_m$, **ऊपरी तरफ का बैंड** $\\omega_c + \\omega_m$। बैंडविड्थ $= 2\\omega_m$ (दो बार उच्चतम संदेश आवृत्ति)।',
            },
            {
              english: 'Over-modulation ($\\mu > 1$) → distortion.',
        hindi: 'अति-मॉड्यूलेशन ($\\mu > 1$) → विरूपण।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Most of the AM transmitted power is in the carrier (carries no info). For $\\mu = 1$, only $\\tfrac13$ of total power is in the side bands — AM is power-inefficient compared to FM.',
        hindi: 'अधिकांश AM संचारित शक्ति वाहक में होती है (कोई जानकारी नहीं होती)। $\\mu = 1$ के लिए, कुल पावर का केवल $\\tfrac13$ साइड बैंड में है - एफएम की तुलना में AM पावर-अक्षम है।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Carrier 1 MHz; modulating audio 0–5 kHz. Bandwidth required for AM transmission?',
        hindi: 'कैरियर 1 मेगाहर्ट्ज; ऑडियो मॉड्यूलेटिंग 0-5 किलोहर्ट्ज़। AM ट्रांसमिशन के लिए बैंडविड्थ आवश्यक है?',
          },
          solution: {
            english: 'BW $= 2 f_m = 10$ kHz.',
        hindi: 'BW $= 2 f_m = 10$ kHz।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'A TV tower of height $80$ m. LOS range for ground-level receiver ($R = 6400$ km)?',
        hindi: 'ऊंचाई $80$ मीटर का एक टीवी टावर। ग्राउंड-लेवल रिसीवर के लिए LOS रेंज ($R = 6400$ किमी)?',
          },
          solution: {
            english: '$d = \\sqrt{2Rh} = \\sqrt{2(6.4{\\times}10^6)(80)} \\approx 32$ km.',
        hindi: '$d = \\sqrt{2Rh} = \\sqrt{2(6.4{\\times}10^6)(80)} \\approx 32$ किमी.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'd = \\sqrt{2Rh}',
      desc: {
        english: 'LOS range (single antenna)',
        hindi: 'LOS रेंज (एकल एंटीना)',
      },
    },
    {
      latex: 'A = 2\\pi R h',
      desc: {
        english: 'Coverage area',
        hindi: 'कवरेज क्षेत्र',
      },
    },
    {
      latex: '\\ell = \\lambda/4',
      desc: {
        english: 'Quarter-wave antenna',
        hindi: 'क्वार्टर-वेव एंटीना',
      },
    },
    {
      latex: '\\mu = A_m/A_c',
      desc: {
        english: 'Modulation index',
        hindi: 'मॉड्यूलेशन इंडेक्स',
      },
    },
    {
      latex: '\\text{BW}_{AM} = 2 f_m',
      desc: {
        english: 'AM bandwidth',
        hindi: 'AM बैंडविड्थ',
      },
    },
    {
      latex: 'f_{LSB} = f_c - f_m,\\ f_{USB} = f_c + f_m',
      desc: {
        english: 'Side bands',
        hindi: 'साइड बैंड',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'AM',
      problem: {
        english: 'A message of frequency 5 kHz amplitude-modulates a 1 MHz carrier. Frequencies present?',
        hindi: '5 किलोहर्ट्ज़ आवृत्ति का एक संदेश 1 मेगाहर्ट्ज वाहक को आयाम-मॉड्यूलेट करता है। आवृत्तियाँ मौजूद हैं?',
      },
      answer: {
        english: '$0.995, 1.000, 1.005$ MHz',
        hindi: '$0.995, 1.000, 1.005$ मेगाहर्ट्ज',
      },
      solution: {
        english: '$f_c, f_c \\pm f_m$.',
        hindi: '$f_c, f_c \\pm f_m$।',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'LOS',
      problem: {
        english: 'Range of TV signal from a tower of height 245 m on flat earth ($R = 6400$ km)?',
        hindi: 'समतल पृथ्वी पर 245 मीटर ऊंचे टावर से टीवी सिग्नल की रेंज ($R = 6400$ किमी)?',
      },
      answer: {
        english: '$\\approx 56$ km',
        hindi: '$\\approx 56$ किमी',
      },
      solution: {
        english: '$d = \\sqrt{2(6.4{\\times}10^6)(245)} \\approx 5.6{\\times}10^4$ m.',
        hindi: '$d = \\sqrt{2(6.4{\\times}10^6)(245)} \\approx 5.6{\\times}10^4$ मी.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Modulation',
      problem: {
        english: '$A_{\\max} = 12$ V, $A_{\\min} = 4$ V. Modulation index?',
        hindi: '$A_{\\max} = 12$ V, $A_{\\min} = 4$ V. मॉड्यूलेशन इंडेक्स?',
      },
      answer: {
        english: '$0.5$',
        hindi: '$0.5$',
      },
      solution: {
        english: '$\\mu = (12-4)/(12+4) = 0.5$.',
        hindi: '$\\mu = (12-4)/(12+4) = 0.5$।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Propagation',
      problem: {
        english: 'Above 40 MHz, which mode of propagation is used?',
        hindi: '40 मेगाहर्ट्ज से ऊपर, प्रसार के किस तरीके का उपयोग किया जाता है?',
      },
      answer: {
        english: 'Space wave / line of sight',
        hindi: 'अंतरिक्ष तरंग/दृष्टि रेखा',
      },
      solution: {
        english: 'Ionosphere does not reflect frequencies above ~30 MHz.',
        hindi: 'आयनमंडल ~30 मेगाहर्ट्ज से ऊपर की आवृत्तियों को प्रतिबिंबित नहीं करता है।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Bandwidth',
      problem: {
        english: 'Audio signal of bandwidth 4.5 kHz amplitude modulates a carrier. AM bandwidth?',
        hindi: 'बैंडविड्थ 4.5 kHz आयाम का ऑडियो सिग्नल एक वाहक को नियंत्रित करता है। एएम बैंडविड्थ?',
      },
      answer: {
        english: '$9$ kHz',
        hindi: '$9$ kHz',
      },
      solution: {
        english: '$2 f_m$.',
        hindi: '$2 f_m$।',
      },
    },
  ],
  next: {
    slug: 'centre-of-mass-collisions',
    title: 'Centre of Mass & Collisions',
  },
};

export default chapter;
